// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { EmptyState } from '@/components/empty-state';
import {
  FilterOption,
  FilterToggleGroup,
} from '@/components/filter-toggle-group';
import { PageContent } from '@/components/page-content';
import { ProjectFulfillmentFilterControl } from '@/components/project-fulfillment-filter-control';
import { SyncStatusFilterMenu } from '@/components/sync-status-filter-menu';
import { TableControlContainer } from '@/components/table-control-container';
import {
  CrmSyncStatus,
  crmSyncStatusSchema,
} from '@/constants/crm-sync-status';
import {
  FulfillmentStatus,
  fulfillmentStatusSchema,
} from '@/constants/fulfillment-status';
import {
  AddRounded,
  CloseRounded,
  ListRounded,
  SettingsRounded,
} from '@/constants/hardfin-standard-icons';
import {
  ProjectCompanyType,
  projectCompanyTypeSchema,
} from '@/constants/project-company-type';
import {
  PROJECT_STATUS_ICONS,
  PROJECT_STATUS_LABELS,
  PROJECT_STATUS_SLUGS,
  ProjectStatusSlug,
  projectStatusSlugSchema,
  toProjectStatus,
} from '@/constants/project-status';
import {
  ProjectViewMode,
  projectViewModeSchema,
} from '@/constants/project-view-mode';
import { SEARCH_TERM_MAXIMUM_CHARACTERS } from '@/constants/search-term';
import {
  Sort,
  SortDirection,
  invert,
  sortDirectionSchema,
} from '@/constants/sort';
import { CustomerGroupedFilterMenu } from '@/customer/components/customer-grouped-filter-menu';
import { WorkflowStageFilter } from '@/filters/workflow-stage-filter';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useLocalStorage } from '@/hooks/use-local-storage';
import { ItemFilterMenu } from '@/items/components/item-filter-menu';
import { LocationFilterMenu } from '@/location/components/location-filter-menu';
import {
  ProjectFilter,
  ProjectSortProperty,
  projectSortPropertySchema,
} from '@/project/api/get-projects';
import { CreateProjectDialog } from '@/project/components/create-project-dialog';
import { ProjectTableView } from '@/project/components/project-table-view';
import { ProjectTimeline } from '@/project/components/project-timeline';
import { ProjectViewModeToggle } from '@/project/components/project-view-mode-toggle';
import { ProjectsFinancialSummary } from '@/project/components/projects-financial-summary';
import { useProjects } from '@/project/hooks/use-projects';
import { searchTermSchema } from '@/utilities/search-term';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Icon,
  IconButton,
  InputAdornment,
  LinearProgress,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
  debounce,
} from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';
import { omit } from 'lodash-es';
import { ChangeEventHandler, Suspense, useMemo, useRef } from 'react';
import { z } from 'zod';

const LOCAL_STORAGE_VIEW_KEY = 'project-view-mode';
const LOCAL_STORAGE_SORT_KEY = 'project-sort';
const DEFAULT_SORT_PROPERTY: ProjectSortProperty = 'name';
const DEFAULT_SORT_DIRECTION: SortDirection = 'asc';
const DEFAULT_VIEW_MODE: ProjectViewMode = 'timeline';
const INCLUDE_ARCHIVED_TOOLTIP_TITLE =
  'Show all project records, including archived projects. Archived projects do not appear by default in the project list.';

// Type for the status filter toggle group that includes 'all' option
type StatusFilterValue = ProjectStatusSlug | 'all';

// Filter options for the status toggle button group
// The 'all' option is added manually since it's not part of the project status schema
const STATUS_FILTER_OPTIONS: FilterOption<StatusFilterValue>[] = [
  {
    value: 'all',
    label: 'All',
    icon: <ListRounded fontSize="small" />,
  },
  ...PROJECT_STATUS_SLUGS.map((slug) => ({
    value: slug,
    label: PROJECT_STATUS_LABELS[toProjectStatus(slug)],
    icon: PROJECT_STATUS_ICONS[toProjectStatus(slug)],
  })),
];

const searchSchema = z.object({
  sort: projectSortPropertySchema.optional().catch(undefined),
  direction: sortDirectionSchema.optional().catch(undefined),
  query: searchTermSchema,
  archived: z.boolean().optional(),
  productFulfillmentStatus: z.array(fulfillmentStatusSchema).optional(),
  projectFulfillmentStatus: z.array(fulfillmentStatusSchema).optional(),
  statuses: z.array(projectStatusSlugSchema).optional().catch(undefined),
  products: z.array(z.string()).optional(),
  companyTypes: z.array(projectCompanyTypeSchema).optional(),
  customers: z.array(z.string()).optional(),
  locations: z.array(z.string()).optional(),
  workflowStageIds: z.array(z.string()).optional(),
  syncStatuses: z.array(crmSyncStatusSchema).optional(),
  view: projectViewModeSchema.optional().catch(undefined),
  page: z.number().optional().catch(undefined),
  new: z.string().optional(),
});

const sortSchema = z.object({
  property: projectSortPropertySchema,
  direction: sortDirectionSchema,
});

export const Route = createFileRoute('/_app/projects/')({
  component: ProjectsRoute,
  validateSearch: searchSchema,
});

const DEFAULT_PAGE_SIZE = 50;

function ProjectsRoute() {
  const searchParameters = Route.useSearch();
  const navigate = Route.useNavigate();
  // Auto-open creation dialog if 'new' parameter is present
  const dialog = useDisclosure(searchParameters.new !== undefined);
  const menu = useDisclosure();
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const [sortPreference, setSortPreference] = useLocalStorage(
    LOCAL_STORAGE_SORT_KEY,
    sortSchema
  );

  const [viewModePreference, setViewModePreference] = useLocalStorage(
    LOCAL_STORAGE_VIEW_KEY,
    projectViewModeSchema
  );

  const currentPage = searchParameters.page ?? 1;

  const updateQuery = useMemo(() => {
    return debounce(
      (query: string) =>
        navigate({ search: { ...searchParameters, query, page: 1 } }),
      300
    );
  }, [navigate, searchParameters]);

  const viewMode =
    searchParameters.view ?? viewModePreference ?? DEFAULT_VIEW_MODE;

  const isArchivedChecked = searchParameters.archived ?? false;
  const projectFulfillmentStatus =
    searchParameters.projectFulfillmentStatus ?? [];
  const statuses = searchParameters.statuses ?? [];
  // Derive toggle value from statuses array: show specific status if exactly one, otherwise 'all'
  const currentStatusFilter: StatusFilterValue =
    statuses.length === 1 ? statuses[0] : 'all';
  const productIds = searchParameters.products ?? [];
  const companyTypes = searchParameters.companyTypes ?? [];
  const customerIds = searchParameters.customers ?? [];
  const locationIds = searchParameters.locations ?? [];
  const workflowStageIds = searchParameters.workflowStageIds ?? [];
  const isBadgeInvisible = searchParameters.archived === undefined;
  const defaultProjectName = searchParameters.new
    ? decodeURIComponent(searchParameters.new)
    : undefined;

  // Convert statuses array for API (undefined means all statuses)
  const statusesForApi: ProjectStatusSlug[] | undefined =
    statuses.length > 0 ? statuses : undefined;

  // Timeline view is incompatible with "No dates set" filter since those projects
  // have no start/end dates to display on the timeline
  const isTimelineIncompatible =
    viewMode === 'timeline' && statuses.includes('date-range-incomplete');

  // Base filter used for table view with pagination
  const baseFilter: ProjectFilter = {
    query: searchParameters.query,
    statuses: statusesForApi,
    isArchived: searchParameters.archived ? 'all' : undefined,
    productFulfillmentStatus: searchParameters.productFulfillmentStatus
      ? searchParameters.productFulfillmentStatus
      : undefined,
    projectFulfillmentStatus: searchParameters.projectFulfillmentStatus
      ? searchParameters.projectFulfillmentStatus
      : undefined,
    productIds: searchParameters.products,
    companyTypes: searchParameters.companyTypes,
    customerIds: searchParameters.customers,
    locationIds: searchParameters.locations,
    workflowStageIds: searchParameters.workflowStageIds,
    syncStatuses: searchParameters.syncStatuses,
    pageSize: DEFAULT_PAGE_SIZE,
    pageNumber: currentPage,
  };

  // Timeline-specific filter with optimization flags
  const timelineFilter: ProjectFilter = {
    ...baseFilter,
    completeDatesOnly: true, // Only projects with start and end dates
    allRecords: true, // Load all records for timeline view
    pageSize: undefined, // Remove pagination for timeline
    pageNumber: undefined,
  };

  // Use appropriate filter based on view mode
  const filter = viewMode === 'timeline' ? timelineFilter : baseFilter;

  // The current sort state is constructed by first checking the URL search
  // parameters. If the search parameters are not present, the sort preference
  // from local storage is used. If the sort preference is not present, the
  // default sort property and direction are used.
  const sort: Sort<ProjectSortProperty> = {
    property:
      searchParameters.sort ??
      sortPreference?.property ??
      DEFAULT_SORT_PROPERTY,
    direction:
      searchParameters.direction ??
      sortPreference?.direction ??
      DEFAULT_SORT_DIRECTION,
  };

  const setSort = (property: ProjectSortProperty, direction: SortDirection) => {
    setSortPreference({ property, direction });
    navigate({
      search: { ...searchParameters, direction, sort: property, page: 1 },
    });
  };

  const handleChangeQuery: ChangeEventHandler<HTMLInputElement> = (event) => {
    updateQuery(event.target.value);
  };
  const searchField = useRef<HTMLInputElement | null>(null);
  const handleClearSearch = () => {
    const currentSearchField = searchField.current;
    if (currentSearchField) {
      currentSearchField.value = '';
    }
    navigate({ search: omit(searchParameters, 'query') });
  };

  const handleChangeArchivedFilter = (_: unknown, isChecked: boolean) => {
    if (isChecked) {
      return navigate({
        search: { ...searchParameters, archived: true, page: 1 },
      });
    }

    navigate({ search: { ...omit(searchParameters, 'archived'), page: 1 } });
  };

  const handleChangeCompany = (
    companyTypes: ProjectCompanyType[],
    customers: string[]
  ) => {
    // Reset to page 1 when filters change since results may be different
    const searchParams = { ...omit(searchParameters, 'page'), page: 1 };

    if (companyTypes.length === 0) {
      delete searchParams.companyTypes;
    } else {
      searchParams.companyTypes = companyTypes;
    }

    if (customers.length === 0) {
      delete searchParams.customers;
    } else {
      searchParams.customers = customers;
    }

    navigate({ search: searchParams });
  };

  const handleChangeLocations = (locations: string[]) => {
    navigate({
      search:
        locations.length === 0
          ? { ...omit(searchParameters, 'locations'), page: 1 }
          : { ...searchParameters, locations, page: 1 },
    });
  };

  const handleChangeProducts = (products: string[]) => {
    navigate({
      search:
        products.length === 0
          ? { ...omit(searchParameters, 'products'), page: 1 }
          : { ...searchParameters, products, page: 1 },
    });
  };

  const handleChangeSyncStatuses = (syncStatuses: CrmSyncStatus[]) => {
    navigate({
      search:
        syncStatuses.length === 0
          ? { ...omit(searchParameters, 'syncStatuses'), page: 1 }
          : { ...searchParameters, syncStatuses, page: 1 },
    });
  };

  const handleChangeStatusFilter = (value: StatusFilterValue) => {
    if (value === 'all') {
      navigate({
        search: { ...omit(searchParameters, 'statuses'), page: 1 },
      });
    } else {
      navigate({
        search: { ...searchParameters, statuses: [value], page: 1 },
      });
    }
  };

  const handleChangeViewMode = (view: ProjectViewMode) => {
    setViewModePreference(view);
    navigate({ search: { ...searchParameters, view } });
  };

  const handleChangeProjectFulfillment = (value: FulfillmentStatus[]) => {
    if (value.length === 0) {
      navigate({
        search: {
          ...omit(searchParameters, 'projectFulfillmentStatus'),
          page: 1,
        },
      });
    } else {
      navigate({
        search: {
          ...searchParameters,
          projectFulfillmentStatus: value,
          page: 1,
        },
      });
    }
  };

  const handleChangeWorkflowStageIds = (workflowStageIds: string[]) => {
    navigate({
      search:
        workflowStageIds.length === 0
          ? { ...omit(searchParameters, 'workflowStageIds'), page: 1 }
          : { ...searchParameters, workflowStageIds, page: 1 },
    });
  };

  const handleClearAllFilters = () => {
    // Clear search field immediately
    const currentSearchField = searchField.current;
    if (currentSearchField) {
      currentSearchField.value = '';
    }

    // Navigate to completely empty state
    navigate({ search: {} });
  };

  const handleSort = (property: ProjectSortProperty) => {
    if (property === sort.property) {
      return setSort(property, invert(sort.direction));
    }

    setSort(property, 'asc');
  };

  const handlePageChange = (page: number) => {
    navigate({ search: { ...searchParameters, page } });
  };

  // Calculate if clear button should be shown based on actual displayed filter values
  // Note: datesFilter is not included here since it's a toggle group, not a dropdown filter
  const showClearButton =
    isArchivedChecked ||
    projectFulfillmentStatus.length > 0 ||
    productIds.length > 0 ||
    customerIds.length > 0 ||
    locationIds.length > 0 ||
    workflowStageIds.length > 0 ||
    (searchParameters.syncStatuses ?? []).length > 0 ||
    (searchParameters.query !== undefined &&
      searchParameters.query.trim() !== '');

  // For timeline view, always sort by start date
  const timelineSort: Sort<ProjectSortProperty> = {
    property: 'start',
    direction: 'asc',
  };

  // Use different sort based on view mode
  const activeSort = viewMode === 'timeline' ? timelineSort : sort;

  // Fetch projects once and share across child components. `projects` and
  // `totalCount` are `null` while the current query key is loading; downstream
  // components render skeletons in that state.
  const {
    data: projects,
    totalCount,
    isLoading: isProjectsLoading,
    isFetching: isProjectsFetching,
  } = useProjects({ sort: activeSort, filter });

  return (
    <PageContent
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CreateProjectDialog
        isOpen={dialog.isOpen}
        onClose={() => {
          dialog.close();
          if (searchParameters.new) {
            navigate({ search: omit(searchParameters, 'new') });
          }
        }}
        initialName={defaultProjectName}
      />
      <ProjectsFinancialSummary filter={filter} />
      <Stack
        useFlexGap
        spacing={2}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <FilterToggleGroup
          value={currentStatusFilter}
          options={STATUS_FILTER_OPTIONS}
          onChange={handleChangeStatusFilter}
          ariaLabel="project status filter"
        />
        <Stack direction="row" spacing={1} alignItems="center">
          <ProjectViewModeToggle
            value={viewMode}
            onChange={handleChangeViewMode}
          />
          <Button
            variant="contained"
            startIcon={<AddRounded />}
            onClick={dialog.open}
          >
            Add project
          </Button>
        </Stack>
      </Stack>

      {/* Table controls */}
      <TableControlContainer>
        <Stack direction="row" marginRight="auto" spacing={1}>
          <CustomerGroupedFilterMenu
            companyTypes={companyTypes}
            customerIds={customerIds}
            onChange={handleChangeCompany}
            includeNotSet={true}
          />
          <WorkflowStageFilter
            label="Stage"
            value={workflowStageIds}
            onChange={handleChangeWorkflowStageIds}
            enableSearch={false}
          />
          <LocationFilterMenu
            value={locationIds}
            onChange={handleChangeLocations}
          />
          <ItemFilterMenu
            label="Products"
            value={productIds}
            type="DEVICE"
            includeArchived={true}
            onChange={handleChangeProducts}
          />
          <ProjectFulfillmentFilterControl
            value={projectFulfillmentStatus}
            onChange={handleChangeProjectFulfillment}
          />
          <SyncStatusFilterMenu
            value={searchParameters.syncStatuses ?? []}
            onChange={handleChangeSyncStatuses}
          />
          {showClearButton && (
            <Button
              onClick={handleClearAllFilters}
              variant="text"
              color="primary"
              size="small"
              sx={{ marginLeft: 2 }}
            >
              Clear filters
            </Button>
          )}
        </Stack>
        <TextField
          size="small"
          label="Search projects"
          defaultValue={searchParameters.query}
          inputProps={{ maxLength: SEARCH_TERM_MAXIMUM_CHARACTERS }}
          onChange={handleChangeQuery}
          sx={{ width: { xs: '100%', sm: '20rem' } }}
          InputProps={{
            inputRef: searchField,
            endAdornment: searchParameters.query ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label="clear search"
                  onClick={handleClearSearch}
                  edge="end"
                  size="small"
                >
                  <CloseRounded fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : (
              <InputAdornment position="end">
                <Icon
                  sx={{
                    fontSize: '1.125rem',
                    p: 0.5,
                    borderRadius: 1,
                    width: '1.75rem',
                    height: '1.75rem',
                  }}
                  fontSize={'small'}
                />
              </InputAdornment>
            ),
          }}
        />
        <IconButton ref={menuButtonRef} onClick={menu.open}>
          <Badge variant="dot" color="error" invisible={isBadgeInvisible}>
            <SettingsRounded fontSize="small" />
          </Badge>
        </IconButton>
        <Popover
          anchorEl={menuButtonRef.current}
          open={menu.isOpen}
          onClose={menu.close}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Stack paddingY={0.5}>
            <Tooltip
              arrow
              placement="left"
              title={INCLUDE_ARCHIVED_TOOLTIP_TITLE}
            >
              <FormControlLabel
                sx={{ paddingLeft: 2, paddingY: 0.5 }}
                label={
                  <Typography variant="body2">
                    Include archived projects
                  </Typography>
                }
                control={
                  <Checkbox
                    size="small"
                    checked={isArchivedChecked}
                    onChange={handleChangeArchivedFilter}
                  />
                }
              />
            </Tooltip>
          </Stack>
        </Popover>
      </TableControlContainer>
      <Box sx={{ position: 'relative' }}>
        {isProjectsFetching && !isProjectsLoading && (
          <LinearProgress
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1,
              borderRadius: '4px 4px 0 0',
            }}
          />
        )}
        <Suspense>
          {viewMode === 'table' && (
            <ProjectTableView
              projects={projects}
              totalCount={totalCount}
              isLoading={isProjectsLoading}
              sort={sort}
              onSort={handleSort}
              onPageChange={handlePageChange}
              currentPage={currentPage}
              pageSize={DEFAULT_PAGE_SIZE}
            />
          )}
          {viewMode === 'timeline' && isTimelineIncompatible && (
            <EmptyState
              title="Timeline not available"
              message="Projects without dates can't be displayed on the timeline. Switch to table view or select a different status filter."
            />
          )}
          {viewMode === 'timeline' && !isTimelineIncompatible && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                height: 'calc(100vh - 9rem)',
                maxHeight: 'calc(100vh - 9rem)',
                overflow: 'hidden',
              }}
            >
              <ProjectTimeline
                isLoading={isProjectsLoading}
                projects={projects ?? []}
              />
            </Box>
          )}
        </Suspense>
      </Box>
    </PageContent>
  );
}
